import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { ChangeType } from '@platform/shared-types';
import {
  Button, FieldLabel, IconCalendar, IconLeft, IconRight, INPUT_CLASS, WarnNote,
} from './ui';

/**
 * The publish gate from mockup screen 4.
 *
 * Five requirements, and change type has NO PRE-SELECTED DEFAULT. That is a
 * deliberate product decision, not an oversight: a default is how every change
 * quietly becomes "minor" and how re-acceptance silently stops happening. The
 * Publish button stays disabled until a human picks one.
 *
 * PURE PRESENTATIONAL. It validates its own fields and hands the result to
 * `onConfirm`; it never calls an API, and it never uses `window.confirm` — the
 * confirmation IS this modal.
 *
 * It DOES report the two fields the blast radius depends on — `changeType` and
 * `effectiveDay` — upward as they are chosen, via two optional callbacks. The
 * state is still owned here; the callbacks fire alongside `setState`. Without
 * them a host can only learn the choice at confirm time, which means the number
 * on screen was computed for a different question than the one being asked, and
 * a future-dated publish shows a radius evaluated at *now*.
 *
 * See the PALETTE RULE in ./ui.tsx before adding a class.
 */

/* ── date helpers (no dependency, no native <input type="date">) ─────────── */

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/** A calendar day, held as 'YYYY-MM-DD'. Effective dates are legal dates, not
 *  instants, so they are never carried around as a local-timezone Date. */
export type CalendarDay = string;

const pad = (n: number) => String(n).padStart(2, '0');

function toDay(t: number): CalendarDay {
  const d = new Date(t);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

function parseDay(day: CalendarDay): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]) - 1, d: Number(m[3]) };
}

function formatDay(day: CalendarDay): string {
  const p = parseDay(day);
  if (!p) return '';
  return `${p.d} ${MONTHS[p.m]} ${p.y}`;
}

/**
 * The instant the version takes effect. A calendar day becomes UTC midnight —
 * one unambiguous instant everywhere, so `activeVersion()` flips at the same
 * moment for every reader regardless of where they are.
 */
export function dayToEffectiveAt(day: CalendarDay): string {
  return `${day}T00:00:00.000Z`;
}

function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
}

function CalendarPopover({
  value, minDay, onPick, onClose,
}: {
  value: CalendarDay | '';
  minDay: CalendarDay;
  onPick: (day: CalendarDay) => void;
  onClose: () => void;
}) {
  const start = parseDay(value || minDay) || parseDay(minDay)!;
  const [view, setView] = useState({ y: start.y, m: start.m });

  const firstWeekday = new Date(Date.UTC(view.y, view.m, 1)).getUTCDay();
  const total = daysInMonth(view.y, view.m);
  const cells: (number | null)[] = [
    ...Array.from({ length: firstWeekday }, (): number | null => null),
    ...Array.from({ length: total }, (_, i) => i + 1),
  ];

  const step = (delta: number) => {
    const m = view.m + delta;
    setView({ y: view.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 });
  };

  return (
    <div className="absolute left-0 right-0 top-full z-10 mt-1 overflow-hidden rounded-lg border border-stone-300 bg-white shadow-2xl">
      {/* Black month bar, matching the app's own header chrome. */}
      <div className="mb-2 flex items-center justify-between bg-stone-900 px-1 text-white">
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => step(-1)}
          className="flex h-11 w-11 items-center justify-center rounded-lg text-stone-300 hover:bg-white/10 hover:text-white"
        >
          <IconLeft size={16} />
        </button>
        <span className="text-[13px] font-semibold text-white">
          {MONTHS[view.m]} {view.y}
        </span>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => step(1)}
          className="flex h-11 w-11 items-center justify-center rounded-lg text-stone-300 hover:bg-white/10 hover:text-white"
        >
          <IconRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 px-3 text-center">
        {WEEKDAYS.map((w) => (
          <span key={w} className="py-1 text-[10px] font-semibold uppercase text-stone-500">
            {w}
          </span>
        ))}
        {cells.map((d, i) => {
          if (d === null) return <span key={`pad-${i}`} />;
          const day = `${view.y}-${pad(view.m + 1)}-${pad(d)}`;
          const disabled = day < minDay;
          const selected = day === value;
          return (
            <button
              key={day}
              type="button"
              disabled={disabled}
              onClick={() => { onPick(day); onClose(); }}
              className={`min-h-[44px] rounded-lg text-[12.5px] transition-colors ${
                selected
                  ? 'bg-red-700 font-bold text-white'
                  : disabled
                    ? 'cursor-not-allowed text-stone-300'
                    // TODAY is ringed even when it is not the chosen day, so a
                    // reader can always place the effective date relative to now.
                    : day === minDay
                      ? 'font-bold text-red-700 ring-1 ring-inset ring-red-300 hover:bg-red-50'
                      : 'text-stone-900 hover:bg-stone-100'
              }`}
            >
              {d}
            </button>
          );
        })}
      </div>

      <div className="mt-2 flex justify-end px-3 pb-2">
        <button
          type="button"
          onClick={onClose}
          className="min-h-[44px] px-3 text-[12px] text-stone-400 hover:text-stone-900"
        >
          Close
        </button>
      </div>
    </div>
  );
}

/* ── the change-type radios ─────────────────────────────────────────────── */

function ChangeTypeRadio({
  selected, title, body, onSelect,
}: { selected: boolean; title: string; body: ReactNode; onSelect: () => void }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
        selected
          ? 'border-red-500 bg-red-500/10'
          : 'border-stone-200 bg-white hover:border-stone-300'
      }`}
    >
      <span
        className={`mt-0.5 h-[15px] w-[15px] shrink-0 rounded-full border-2 ${
          selected ? 'border-red-500 bg-red-500 ring-2 ring-inset ring-dark-900' : 'border-stone-300'
        }`}
        aria-hidden="true"
      />
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold text-stone-900">{title}</span>
        <span className="mt-1 block text-[11.5px] leading-relaxed text-stone-500">{body}</span>
      </span>
    </button>
  );
}

/* ── the modal ──────────────────────────────────────────────────────────── */

export interface PublishConfirmInput {
  /** ISO instant — UTC midnight of the chosen calendar day. */
  effectiveAt: string;
  /** 'YYYY-MM-DD', the day the user actually picked. */
  effectiveDay: CalendarDay;
  changeType: ChangeType;
  /** Present and non-empty whenever `changeType` is 'material'. */
  changeSummary?: string;
}

export interface PublishModalProps {
  documentTitle: string;
  versionNo: number;
  /** Injected clock; also the earliest selectable effective day. */
  nowIso: string;
  approvedByName?: string;
  approvedAtIso?: string;
  changedBlockCount?: number;
  /**
   * ABSOLUTE, NOT A DELTA. How many acceptances are stale AFTER this publish —
   * which includes anyone already stranded by an EARLIER material version, not
   * only the parties this publish strands. That is exactly what
   * `staleAcceptanceCount()` in shared-types computes, and it is why the number
   * is rendered for minor publishes too: a minor change strands nobody new, but
   * it does not clear anybody either.
   *
   * Computed by the host and passed in — the modal performs no I/O of its own.
   *
   * OMIT IT (leave `undefined`) whenever the number is unknown, in flight, or
   * was computed for a different change type or effective date than the one now
   * selected. The modal then renders an explicit "—" rather than a "0" that
   * would read as "this affects nobody".
   */
  staleAcceptanceCount?: number;
  /** Free-text breakdown under the number, e.g. "318 tenant admins and 23 employees". */
  blastRadiusDetail?: ReactNode;
  submitting?: boolean;
  /** Server-side failure, rendered inline. Never an alert. */
  error?: string | null;
  onCancel: () => void;
  onConfirm: (input: PublishConfirmInput) => void;
  /**
   * Fired whenever the user picks a change type. The host needs this to re-fetch
   * the blast radius for the choice ACTUALLY being made — without it the number
   * on screen can answer a different question than the radio.
   *
   * Optional, and the internal state is NOT lifted out: existing callers keep
   * working untouched.
   */
  onChangeTypeChange?: (changeType: ChangeType | null) => void;
  /**
   * Fired whenever the user picks an effective date ('YYYY-MM-DD'). A publish
   * dated in the future must have its blast radius evaluated at THAT date, not
   * at now, so the host needs the day as it is chosen — not only at confirm.
   */
  onEffectiveDayChange?: (day: CalendarDay) => void;
}

export function PublishModal({
  documentTitle, versionNo, nowIso, approvedByName, approvedAtIso,
  changedBlockCount, staleAcceptanceCount, blastRadiusDetail,
  submitting = false, error = null, onCancel, onConfirm,
  onChangeTypeChange, onEffectiveDayChange,
}: PublishModalProps) {
  const titleId = useId();
  const dateId = useId();
  const summaryId = useId();

  const today = useMemo(() => toDay(Date.parse(nowIso)), [nowIso]);
  /**
   * DEFAULTS TO TODAY, rather than to nothing.
   *
   * Publishing effective today is what an operator means the overwhelming
   * majority of the time, and an empty field made every publish a two-step
   * dance through a calendar to pick the date it already was. The date is
   * still fully editable, and the blast radius is still computed against
   * whatever is chosen — so defaulting it changes the effort, not the
   * decision.
   */
  const [day, setDay] = useState<CalendarDay | ''>(today);
  // NO DEFAULT. Deliberate — see the file header.
  const [changeType, setChangeType] = useState<ChangeType | null>(null);
  const [summary, setSummary] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (calendarOpen) setCalendarOpen(false);
      else onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [calendarOpen, onCancel]);

  useEffect(() => {
    if (!calendarOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setCalendarOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [calendarOpen]);

  /*
   * The state stays INTERNAL — these two just report it upward as well, from
   * the very same handlers. Lifting it out would break every caller that does
   * not pass the callbacks; reporting it costs nothing and lets the host
   * re-compute the blast radius for the choice actually on screen.
   */
  const pickChangeType = (next: ChangeType) => {
    setChangeType(next);
    onChangeTypeChange?.(next);
  };

  const pickDay = (next: CalendarDay) => {
    setDay(next);
    onEffectiveDayChange?.(next);
  };

  const needsSummary = changeType === 'material';
  const problems: string[] = [];
  if (!day) problems.push('Pick an effective date.');
  if (!changeType) problems.push('Choose whether this change is minor or material.');
  if (needsSummary && summary.trim().length === 0) {
    problems.push('A material change needs a summary — users read it verbatim.');
  }
  const valid = problems.length === 0;

  // The Publish button is disabled while invalid, so the checklist below is what
  // tells the user WHY — shown as soon as they touch anything, never as an alert.
  const started = !!day || !!changeType || summary.trim().length > 0;

  const confirm = () => {
    if (!valid || !day || !changeType) return;
    onConfirm({
      effectiveAt: dayToEffectiveAt(day),
      effectiveDay: day,
      changeType,
      changeSummary: summary.trim() ? summary.trim() : undefined,
    });
  };

  const futureDated = !!day && day > today;
  /*
   * `undefined` means UNKNOWN — in flight, failed, or computed for a different
   * selection. It must never collapse to 0: "0 acceptances stale" is precisely
   * the sentence that gets a material change published in the belief that it
   * strands nobody.
   */
  const radiusKnown = typeof staleAcceptanceCount === 'number';
  const stale = staleAcceptanceCount ?? 0;
  const material = changeType === 'material';

  const subtitleBits: string[] = [];
  if (approvedByName) {
    subtitleBits.push(
      approvedAtIso
        ? `Approved by ${approvedByName} on ${formatDay(toDay(Date.parse(approvedAtIso)))}`
        : `Approved by ${approvedByName}`,
    );
  }
  if (typeof changedBlockCount === 'number') {
    subtitleBits.push(`${changedBlockCount} block${changedBlockCount === 1 ? '' : 's'} changed`);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-[620px] rounded-xl border border-stone-200 bg-white shadow-2xl"
      >
        <div className="border-b border-stone-200 px-5 py-4">
          <h3 id={titleId} className="text-base font-semibold text-stone-900">
            Publish {documentTitle} v{versionNo}
          </h3>
          {subtitleBits.length > 0 && (
            <p className="mt-1 text-[12.5px] text-stone-600">{subtitleBits.join(' · ')}</p>
          )}
        </div>

        <div className="space-y-5 px-5 py-5">
          {/* effective date */}
          <div>
            <FieldLabel htmlFor={dateId} requirement="required">Effective date</FieldLabel>
            <div ref={wrapRef} className="relative">
              {/* Never a native <input type="date"> — a hand-rolled calendar so
                  the control looks and behaves the same on iOS and desktop. */}
              <button
                id={dateId}
                type="button"
                onClick={() => setCalendarOpen((o) => !o)}
                aria-haspopup="dialog"
                aria-expanded={calendarOpen}
                className={`${INPUT_CLASS} flex items-center justify-between text-left`}
              >
                <span className={day ? 'text-stone-900' : 'text-stone-500'}>
                  {day ? formatDay(day) : 'Choose a date'}
                </span>
                <IconCalendar size={16} className="text-stone-400" />
              </button>
              {calendarOpen && (
                <CalendarPopover
                  value={day}
                  minDay={today}
                  onPick={pickDay}
                  onClose={() => setCalendarOpen(false)}
                />
              )}
            </div>
            {futureDated && (
              <p className="mt-1.5 text-[11.5px] text-stone-500">
                Future-dated. {versionNo > 1 ? `v${versionNo - 1}` : 'The current version'} stays
                live until this date — no scheduler involved, the active version is derived at
                read time.
              </p>
            )}
          </div>

          {/* change type — no pre-selection */}
          <div>
            <FieldLabel requirement="required · no default">Change type</FieldLabel>
            <div className="grid gap-2" role="radiogroup" aria-label="Change type">
              <ChangeTypeRadio
                selected={changeType === 'minor'}
                title="Minor"
                body={
                  <>
                    Typo, formatting, broken link. Existing acceptances stay valid — a minor
                    publish strands <b className="text-stone-900">nobody new</b>. It does not
                    clear anybody either: whoever is already stale from an earlier material
                    version stays stale, so the count below can be above zero.
                  </>
                }
                onSelect={() => pickChangeType('minor')}
              />
              <ChangeTypeRadio
                selected={changeType === 'material'}
                title="Material"
                body="New term, new fee, new data use. Every acceptance older than this version goes stale, and those parties must re-accept before continuing."
                onSelect={() => pickChangeType('material')}
              />
            </div>
          </div>

          {/* summary */}
          <div>
            <FieldLabel
              htmlFor={summaryId}
              requirement="required for material · shown to users verbatim"
            >
              Summary of changes
            </FieldLabel>
            <textarea
              id={summaryId}
              rows={3}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="What changed, in the words users will read."
              className={`${INPUT_CLASS} resize-y leading-relaxed`}
            />
          </div>

          {/*
            BLAST RADIUS — shown for BOTH change types, deliberately.
            The old build rendered it only under Material and told the Minor
            branch "0 parties affected", which is false: staleness carries over
            from earlier material versions and a minor publish does not clear it.

            The number is ABSOLUTE — "stale after this publish", i.e. everyone
            who must re-accept once this version is live, not the subset this
            publish strands. Those are different numbers and the wording is the
            whole point; do not shorten it back to "affected by this change".
          */}
          {changeType && (
            <div
              className={`flex items-center gap-4 rounded-lg border p-3.5 ${
                !radiusKnown
                  ? 'border-stone-200 bg-white'
                  : material || stale > 0
                    ? 'border-red-500/30 bg-red-500/10'
                    : 'border-stone-200 bg-white'
              }`}
            >
              <span
                className={`text-3xl font-bold leading-none ${
                  !radiusKnown ? 'text-stone-500' : material || stale > 0 ? 'text-red-700' : 'text-stone-700'
                }`}
              >
                {radiusKnown ? stale : '—'}
              </span>
              <span className="text-[12.5px] leading-relaxed text-stone-600">
                <b className={!radiusKnown ? 'text-stone-500' : material || stale > 0 ? 'text-red-700' : 'text-stone-900'}>
                  {radiusKnown
                    ? `${stale} acceptance${stale === 1 ? '' : 's'} stale after this publish.`
                    : 'Acceptances stale after this publish: not yet known.'}
                </b>
                <br />
                {blastRadiusDetail
                  || (material
                    ? 'Affected parties hit a blocking "Review and accept the updated document" screen at next login.'
                    : 'A minor publish strands nobody new. Anyone counted here was already stale from an earlier material version.')}
                <span className="mt-1 block text-[11px] text-stone-500">
                  Counted as of {day ? formatDay(day) : 'the effective date, once picked'} — the total
                  who must re-accept once this version is live, not only those stranded by it.
                </span>
              </span>
            </div>
          )}

          <WarnNote>
            On confirm, in one transaction: freeze HTML + PDF, SHA-256 both, stamp the renderer
            version, write the audit event, queue notifications. A version is never
            published-but-unrendered.
          </WarnNote>

          {started && problems.length > 0 && (
            <ul className="space-y-1 rounded-lg border border-red-500/30 bg-red-500/10 px-3.5 py-3 text-[12px] text-red-300">
              {problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}

          {error && (
            <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3.5 py-3 text-[12px] text-red-300">
              {error}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-3 border-t border-stone-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-[11.5px] text-stone-500">
            Publishing cannot be undone. v{versionNo} becomes immutable.
          </span>
          <div className="flex gap-2">
            <Button onClick={onCancel} disabled={submitting}>Cancel</Button>
            <Button
              variant="primary"
              /* Publishing is THE primary action on a ruby screen, and the
                 package's `primary` is emerald. Overridden here rather than in
                 `ui.tsx`, because platform-app compiles the same component and
                 does not share this palette — see the PALETTE RULE there. */
              className="!border-red-700 !bg-red-700 !text-white hover:!bg-red-800"
              onClick={confirm}
              disabled={submitting || !valid}
              aria-disabled={submitting || !valid}
            >
              {submitting ? 'Publishing…' : 'Publish'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
